/**
 * Suppress benign ResizeObserver loop errors in development.
 *
 * Browsers (and CRA's dev overlay) treat "ResizeObserver loop completed with
 * undelivered notifications" as an uncaught error. It is commonly triggered by
 * Bootstrap/React-Bootstrap form validation feedback inside modals — not app bugs.
 *
 * Loaded via side-effect import before other app modules (see index.js).
 * Also mirrored inline in public/index.html so it runs before webpack-dev-server.
 */

export const RESIZE_OBSERVER_LOOP_RE =
  /ResizeObserver loop (completed with undelivered notifications|limit exceeded)/;

let installed = false;

/**
 * Defer ResizeObserver callbacks to the next frame to avoid loop-limit errors.
 */
export function patchResizeObserverCallbacks() {
  if (typeof window === 'undefined' || !window.ResizeObserver) {
    return;
  }

  const Original = window.ResizeObserver;
  if (Original.__materiahPatched) {
    return;
  }

  window.ResizeObserver = class ResizeObserver extends Original {
    constructor(callback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => {
          callback(entries, observer);
        });
      });
    }
  };
  window.ResizeObserver.__materiahPatched = true;
}

/**
 * CRA / react-error-overlay exposes a global hook; patch handleError when present.
 */
export function patchReactErrorOverlayHook() {
  if (typeof window === 'undefined') {
    return;
  }

  const hook = window.__REACT_ERROR_OVERLAY_GLOBAL_HOOK__;
  if (!hook || hook.__materiahResizePatch) {
    return;
  }

  if (typeof hook.handleError === 'function') {
    const original = hook.handleError.bind(hook);
    hook.handleError = error => {
      if (isResizeObserverLoopMessage(error?.message || error)) {
        return;
      }
      return original(error);
    };
  }

  hook.__materiahResizePatch = true;
}

/**
 * @param {unknown} value
 * @returns {boolean}
 */
function isResizeObserverLoopMessage(value) {
  if (typeof value === 'string') {
    return RESIZE_OBSERVER_LOOP_RE.test(value);
  }
  if (value instanceof Error && typeof value.message === 'string') {
    return RESIZE_OBSERVER_LOOP_RE.test(value.message);
  }
  return false;
}

/**
 * Install window/console filters once. Safe to call multiple times.
 */
export function suppressResizeObserverLoopError() {
  if (installed || typeof window === 'undefined') {
    return;
  }
  installed = true;

  patchResizeObserverCallbacks();

  patchReactErrorOverlayHook();
  window.addEventListener('DOMContentLoaded', patchReactErrorOverlayHook);
  window.setTimeout(patchReactErrorOverlayHook, 0);
  window.setTimeout(patchReactErrorOverlayHook, 500);

  window.addEventListener(
    'error',
    event => {
      const message = event?.message || event?.error?.message || '';
      if (isResizeObserverLoopMessage(message)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return false;
      }
      return undefined;
    },
    true,
  );

  const originalConsoleError = console.error;
  console.error = (...args) => {
    if (args.some(arg => isResizeObserverLoopMessage(arg))) {
      return;
    }
    originalConsoleError.apply(console, args);
  };
}
